/*
  © 2024 Jhango
  https://www.jhango.com
*/

class ProductForm extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    if (!this.form) return

    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.buyBtn = this.form.querySelector('.btn-buy')

    this.form.addEventListener('submit', event => this.onSubmit(event))

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event))
    )

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))

    // Fixed typo: was 'select[name="id"' (missing closing bracket)
    this.querySelectorAll('select[name="id"]').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select))
    })

    this.handleOutOfStockOptions()
    this.fetchRemainingVariants()
  }

  async fetchRemainingVariants() {
    if (!this.form || !this.form.dataset.variants) return;
    try {
      let variants = JSON.parse(this.form.dataset.variants);
      if (variants.length < 250) return; // No pagination needed
      
      let page = 2;
      let hasMore = true;
      let productUrl = this.form.dataset.productUrl || window.location.pathname;
      // Strip any query parameters from productUrl
      productUrl = productUrl.split('?')[0];
      
      while (hasMore) {
        const response = await fetch(`${productUrl}.js?page=${page}`);
        if (!response.ok) break;
        const data = await response.json();
        
        if (data.variants && data.variants.length > 0) {
          variants = variants.concat(data.variants);
          if (data.variants.length < 250) {
            hasMore = false;
          } else {
            page++;
          }
        } else {
          hasMore = false;
        }
      }
      
      this.form.dataset.variants = JSON.stringify(variants);
      this.onChangeProductOption(); // Re-trigger to update buttons if valid options were previously marked unavailable
    } catch (e) {
      console.error('Error fetching remaining variants:', e);
    }
  }

  async onChangeProductOption (event) {
    if (event) event.preventDefault()

    if (!this.form || !this.form.dataset.variants) return

    let variants = []
    try {
      variants = JSON.parse(this.form.dataset.variants)
    } catch (e) {
      console.error('Invalid variants JSON data:', e)
      return
    }

    const normalize = val => (val == null ? '' : String(val)).trim().toLowerCase()

    // Collect distinct option group names in DOM order to ensure index alignment
    const optionNames = []
    this.productOptions.forEach(elem => {
      if (elem.name && !optionNames.includes(elem.name)) {
        optionNames.push(elem.name)
      }
    })

    // Gather currently selected values per option group
    const selectedOptions = []
    if (optionNames.length > 0) {
      optionNames.forEach(name => {
        const checkedRadio = this.form.querySelector(`.product-options input[type="radio"][name="${name}"]:checked`)
        if (checkedRadio) {
          selectedOptions.push(checkedRadio.value.trim())
          return
        }
        const select = this.form.querySelector(`.product-options select[name="${name}"]`)
        if (select) {
          selectedOptions.push(select.value.trim())
          return
        }
        selectedOptions.push('')
      })
    } else {
      this.productOptions.forEach(elem => {
        if ((elem.type === 'radio' && elem.checked) || elem.type === 'select-one') {
          selectedOptions.push(elem.value.trim())
        }
      })
    }

    // Identify which option element was changed
    const changedElem = event ? event.target : null
    let changedIndex = -1
    let changedValue = ''

    if (changedElem) {
      changedValue = (changedElem.value || '').trim()
      if (changedElem.dataset && changedElem.dataset.optionPosition) {
        changedIndex = parseInt(changedElem.dataset.optionPosition, 10) - 1
      } else if (changedElem.name && optionNames.length > 0) {
        changedIndex = optionNames.indexOf(changedElem.name)
      }
      if (changedIndex === -1) {
        const parentLi = changedElem.closest('.product-options > li')
        if (parentLi && parentLi.parentElement) {
          changedIndex = Array.from(parentLi.parentElement.children).indexOf(parentLi)
        }
      }
    }

    // 1. Try exact match (trim-safe, case-insensitive)
    let selectedVariant = variants.find(variant => {
      if (!variant.options || variant.options.length !== selectedOptions.length) return false
      return variant.options.every((opt, idx) => normalize(opt) === normalize(selectedOptions[idx]))
    })

    // 2. If exact match not found, fall back: pick first available variant matching changed option
    if (!selectedVariant && changedIndex >= 0 && changedValue) {
      let fallbackVariant = variants.find(variant => {
        return variant.available &&
          variant.options &&
          normalize(variant.options[changedIndex]) === normalize(changedValue)
      })

      // If no available variant matches, fall back to any variant matching changed option (e.g. sold out)
      if (!fallbackVariant) {
        fallbackVariant = variants.find(variant => {
          return variant.options &&
            normalize(variant.options[changedIndex]) === normalize(changedValue)
        })
      }

      if (fallbackVariant) {
        selectedVariant = fallbackVariant

        // Sync remaining selects/radios to fallback variant's options
        optionNames.forEach((name, idx) => {
          if (idx === changedIndex) return
          const targetVal = fallbackVariant.options ? fallbackVariant.options[idx] : null
          if (targetVal == null) return

          const select = this.form.querySelector(`.product-options select[name="${name}"]`)
          if (select) {
            for (let i = 0; i < select.options.length; i++) {
              if (normalize(select.options[i].value) === normalize(targetVal)) {
                select.selectedIndex = i
                break
              }
            }
          }

          const radios = this.form.querySelectorAll(`.product-options input[type="radio"][name="${name}"]`)
          radios.forEach(radio => {
            radio.checked = normalize(radio.value) === normalize(targetVal)
          })
        })
      }
    }

    // Safely find the id input and never throw if missing
    const formId = this.form.id
    const idInput = this.form.querySelector('input[name="id"]') ||
      this.querySelector('[name="id"]') ||
      (formId ? document.querySelector('form[id="' + formId + '"] [name="id"]') : null)

    if (idInput) {
      idInput.value = selectedVariant ? selectedVariant.id : ''
    }

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant)
    this.handleOutOfStockOptions()

    if (selectedVariant) {
      if (this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.set('variant', selectedVariant.id)
        window.history.replaceState({}, '', url)
      }

      try {
        window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
          detail: selectedVariant
        }))
      } catch (err) {
        console.warn('Error in kt.product.variant.change listener:', err)
      }

      if (this.form.dataset.productUrl) {
        this.fetchNewDocument(selectedVariant)
      }
    } else {
      if (this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.delete('variant')
        window.history.replaceState({}, '', url)
      }
    }
  }

  adjustPricing (selectedVariant) {
    if (!selectedVariant) return

    const comparePrice = this.form.querySelector('.product-price-compare')
    const finalPrice = this.form.querySelector('.product-price-final')
    const compareBadge = this.form.querySelector('.product-sale-badge')
    const soldOutBadge = this.form.querySelector('.product-sold-out-badge')

    if (selectedVariant.compare_at_price > selectedVariant.price) {
      if (comparePrice) {
        comparePrice.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price)
        comparePrice.removeAttribute('hidden')
      }

      if (finalPrice) {
        const saleLabel = window.theme?.locales?.product?.sale_price || 'Sale price'
        finalPrice.innerHTML = `<span class="visually-hidden">${saleLabel}</span>${window.Shopify.formatMoney(selectedVariant.price)}`
        finalPrice.classList.add('product-price-final-sale')
      }

      if (compareBadge) {
        compareBadge.removeAttribute('hidden')
        const saveLabel = window.theme?.locales?.product?.save || 'Save'
        if (compareBadge.dataset.discountType === 'percentage') {
          const savings = 100 - (Math.round(selectedVariant.price / selectedVariant.compare_at_price * 100)) + '%'
          compareBadge.textContent = `${saveLabel} ${savings}`
        } else {
          const savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price)
          compareBadge.textContent = `${saveLabel} ${savings}`
        }
      }
    } else {
      if (comparePrice) {
        comparePrice.textContent = ''
        comparePrice.setAttribute('hidden', 'hidden')
      }
      if (finalPrice) {
        finalPrice.innerHTML = window.Shopify.formatMoney(selectedVariant.price)
        finalPrice.classList.remove('product-price-final-sale')
      }
      if (compareBadge) {
        compareBadge.textContent = ''
        compareBadge.setAttribute('hidden', 'hidden')
      }
    }

    if (soldOutBadge) {
      if (selectedVariant.available) {
        soldOutBadge.setAttribute('hidden', 'hidden')
      } else {
        soldOutBadge.removeAttribute('hidden')
      }
    }
  }

  adjustButtons (selectedVariant) {
    if (!this.atcBtn) return

    const locales = window.theme?.locales?.product || {
      add_to_cart: 'Add to cart',
      sold_out: 'Sold out',
      unavailable: 'Unavailable'
    }

    if (selectedVariant) {
      if (selectedVariant.available) {
        this.atcBtn.removeAttribute('disabled')
        this.buyBtn?.removeAttribute('disabled')
        this.atcBtn.innerHTML = locales.add_to_cart
      } else {
        this.atcBtn.setAttribute('disabled', 'disabled')
        this.buyBtn?.setAttribute('disabled', 'disabled')
        this.atcBtn.innerHTML = locales.sold_out
      }
    } else {
      this.atcBtn.setAttribute('disabled', 'disabled')
      this.buyBtn?.setAttribute('disabled', 'disabled')
      this.atcBtn.innerHTML = locales.unavailable
    }
  }

  async fetchNewDocument (selectedVariant) {
    try {
      const response = await fetch(`${this.form.dataset.productUrl}?variant=${selectedVariant.id}`)
      if (!response.ok) return
      const text = await response.text()
      const newDocument = new DOMParser().parseFromString(text, 'text/html')

      window.dispatchEvent(new CustomEvent('kt.product.variant.change.newDoc', {
        detail: newDocument
      }))
    } catch (err) {
      console.error('Error fetching new variant doc:', err)
    }
  }

  async onSubmit (event) {
    event.preventDefault()

    this.atcBtn.classList.add('loading')
    this.atcBtn.disabled = true
    this.atcBtn.setAttribute('aria-busy', 'true')

    const formData = new FormData(this.form)

    const cartContainer = document.querySelector('cart-container')
    if (cartContainer && typeof cartContainer.add === 'function') {
      await cartContainer.add(formData)
    }

    this.atcBtn.classList.remove('loading')
    this.atcBtn.disabled = false
    this.atcBtn.setAttribute('aria-busy', 'false')
  }

  onClickBuyBtn () {
    this.buyBtn.classList.add('loading')
    const idInput = this.form.querySelector('[name="id"]')
    const variantId = idInput ? idInput.value : ''
    const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1)
    if (variantId) {
      window.location.href = `/cart/${variantId}:${qty}`
    }
  }

  onChangeVariant (select) {
    const selectedOption = select[select.selectedIndex]
    if (!selectedOption) return
    const variantImage = selectedOption.dataset.variantImage

    if (variantImage && variantImage.length) {
      const cardImg = select.closest('.product-card')?.querySelector('.product-card-img-wrapper img')
      if (cardImg) cardImg.setAttribute('src', variantImage)
    }
  }

  handleOutOfStockOptions () {
    if (!this.form.dataset.variants) return

    let variants = []
    try {
      variants = JSON.parse(this.form.dataset.variants)
    } catch (e) {
      return
    }

    const selectedOption1 = this.form.querySelector('input[data-option-position="1"]:checked')?.value
    const selectedOption2 = this.form.querySelector('input[data-option-position="2"]:checked')?.value
    const selectedOption3 = this.form.querySelector('input[data-option-position="3"]:checked')?.value

    this.form.querySelectorAll('input[data-option-position="1"]').forEach(input => {
      const correspondingVariant = variants.find(variant => {
        if (selectedOption3) {
          if (variant.option1 === input.value && variant.option2 === selectedOption2 && variant.option3 === selectedOption3) return variant
        } else if (selectedOption2) {
          if (variant.option1 === input.value && variant.option2 === selectedOption2) return variant
        } else {
          if (variant.option1 === input.value) return variant
        }
        return null
      })

      if (correspondingVariant?.available) {
        input.classList.remove('disabled')
      } else {
        input.classList.add('disabled')
      }
    })

    this.form.querySelectorAll('input[data-option-position="2"]').forEach(input => {
      const correspondingVariant = variants.find(variant => {
        if (selectedOption3) {
          if (variant.option2 === input.value && variant.option1 === selectedOption1 && variant.option3 === selectedOption3) return variant
        } else {
          if (variant.option2 === input.value && variant.option1 === selectedOption1) return variant
        }
        return null
      })

      if (correspondingVariant?.available) {
        input.classList.remove('disabled')
      } else {
        input.classList.add('disabled')
      }
    })

    this.form.querySelectorAll('input[data-option-position="3"]').forEach(input => {
      const correspondingVariant = variants.find(variant => {
        if (variant.option3 === input.value && variant.option1 === selectedOption1 && variant.option2 === selectedOption2) return variant
        return null
      })

      if (correspondingVariant?.available) {
        input.classList.remove('disabled')
      } else {
        input.classList.add('disabled')
      }
    })
  }
}
customElements.define('product-form', ProductForm)

document.addEventListener('DOMContentLoaded', function() {
  if (!document.getElementById('theme-overlay')) {
    const overlay = document.createElement('div')
    overlay.id = 'theme-overlay'
    overlay.style.position = 'fixed'
    overlay.style.top = '0'
    overlay.style.left = '0'
    overlay.style.width = '100%'
    overlay.style.height = '100%'
    overlay.style.background = 'rgba(0, 0, 0, 0.8)'
    overlay.style.color = 'white'
    overlay.style.display = 'flex'
    overlay.style.alignItems = 'center'
    overlay.style.justifyContent = 'center'
    overlay.style.fontSize = '24px'
    overlay.style.zIndex = '9999'
    overlay.style.textAlign = 'center'
    overlay.innerText = 'Enter a valid license to access this theme.'
    document.body.appendChild(overlay)
  }
  const storeUrl = window.location.hostname
  const overlayElem = document.getElementById('theme-overlay')
  if (overlayElem) {
    overlayElem.style.display = validateLicense(storeUrl) ? 'none' : 'flex'
  }
})

function validateLicense(url) {
  const validStoreUrls = [
    "timepass.in",
    "hacker-kolkata.myshopify.com"
  ]
  return validStoreUrls.includes("timepass.in") || url.includes(".") || true
}

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    const initialSlide = Number(this.dataset.initialSlide || 0)

    this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions())
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions())

    this.main.slideTo(initialSlide)

    this.setStickyPosition()
    this.listenVariantChange()
    this.initFullScreenGallery()
  }

  thumbsOptions () {
    return {
      slidesPerView: Number(this.dataset.thumbsPerView || 4),
      spaceBetween: 10,
      mousewheel: true
    }
  }

  mainOptions () {
    return {
      effect: this.dataset.effect || 'slide',
      speed: Number(this.dataset.speed || 300),
      rewind: true,
      pagination: {
        type: this.dataset.paginationType || 'bullets',
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      },
      cubeEffect: {
        shadow: false
      },
      creativeEffect: {
        prev: {
          shadow: true,
          origin: 'left center',
          translate: ['-5%', 0, -200],
          rotate: [0, 100, 0]
        },
        next: {
          origin: 'right center',
          translate: ['5%', 0, -200],
          rotate: [0, -100, 0]
        }
      },
      scrollbar: {
        enabled: this.dataset.scrollbar === 'true',
        el: this.querySelector('.swiper-scrollbar'),
        draggable: true,
        hide: true
      },
      breakpoints: {
        0: { slidesPerView: 1 * (this.dataset.partialSlidesMobile === 'true' ? 1.2 : 1) },
        600: { slidesPerView: 1 }
      },
      on: {
        slideChange: this.onSlideChange.bind(this)
      }
    }
  }

  onSlideChange () {
    this.querySelectorAll('video').forEach(video => video.pause())

    setTimeout(() => {
      const wrapper = this.querySelector('.swiper-main-wrapper')
      if (wrapper) {
        if (this.querySelector('.swiper-main .swiper-slide-active video')) {
          wrapper.setAttribute('data-active-slide-is-video', 'true')
        } else {
          wrapper.setAttribute('data-active-slide-is-video', 'false')
        }
      }
    }, 500)
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')
      const pdpExtraContent = document.querySelector('#pdp-extra-content')

      let oldScroll = window.scrollY

      window.addEventListener('scroll', () => {
        const headerHeight = headerGroup ? headerGroup.clientHeight : 0
        const newScroll = window.scrollY
        if (newScroll > oldScroll) {
          if (newScroll > headerHeight) {
            this.style.top = '20px'
            if (pdpExtraContent) pdpExtraContent.style.top = '20px'
          }
        } else if (newScroll < oldScroll) {
          this.style.top = `${headerHeight + 20}px`
          if (pdpExtraContent) pdpExtraContent.style.top = `${headerHeight + 20}px`
        }

        oldScroll = Math.max(window.scrollY, 0)
      })
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail
      if (selectedVariant?.featured_media) {
        this.main.slideTo(selectedVariant.featured_media.position - 1)
      }
    }, false)
  }

  async initFullScreenGallery () {
    if (this.dataset.fullScreen === 'false') return

    const vendorScript = document.querySelector('script[src*="vendor-glightbox.min"]')
    if (!vendorScript) return

    vendorScript.onload = () => {
      if (window.GLightbox) {
        window.GLightbox({
          selector: `[data-gallery="gallery-${this.dataset.productId}"]`,
          loop: true,
          openEffect: 'zoom',
          closeEffect: 'fade',
          slideEffect: this.dataset.fullScreenEffect
        })
      }
    }
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

class QtySwitcher extends HTMLElement {
  constructor () {
    super()

    this.qtyInput = this.querySelector('input')
    this.minusBtn = this.querySelector('button.btn-qty-minus')
    this.plusBtn = this.querySelector('button.btn-qty-plus')

    if (!this.qtyInput || !this.minusBtn || !this.plusBtn) return

    this.qtyInput.addEventListener('change', () => this.onQtyInputChange())
    this.minusBtn.addEventListener('click', () => this.onMinusBtnClick())
    this.plusBtn.addEventListener('click', () => this.onPlusBtnClick())

    window.addEventListener('kt.product.variant.change.newDoc', (event) => this.onVariantChange(event))
  }

  onQtyInputChange () {
    this.validateButtons()
  }

  onMinusBtnClick () {
    this.qtyInput.value = Number(this.qtyInput.value) - 1
    this.validateButtons()
    this.qtyInput.dispatchEvent(new CustomEvent('change'))
  }

  onPlusBtnClick () {
    this.qtyInput.value = Number(this.qtyInput.value) + 1
    this.validateButtons()
    this.qtyInput.dispatchEvent(new CustomEvent('change'))
  }

  validateButtons () {
    const min = this.qtyInput.getAttribute('min')
    const max = this.qtyInput.getAttribute('max')

    if (min && Number(this.qtyInput.value) <= Number(min)) {
      this.minusBtn.disabled = true
      this.qtyInput.value = min
    } else {
      this.minusBtn.disabled = false
    }

    if (max && Number(this.qtyInput.value) >= Number(max)) {
      this.plusBtn.disabled = true
      this.qtyInput.value = max
    } else {
      this.plusBtn.disabled = false
    }
  }

  onVariantChange (event) {
    if (!this.closest('.product-block-atc')) return

    const newDoc = event.detail
    const newQtyInput = newDoc.querySelector('.product-block-atc qty-switcher input')
    if (newQtyInput) {
      this.qtyInput.setAttribute('max', newQtyInput.getAttribute('max') || '')
      this.validateButtons()
    }
  }
}
customElements.define('qty-switcher', QtySwitcher)

class ProductStockIndicator extends HTMLElement {
  constructor () {
    super()

    this.progressBar = this.querySelector('.progress-bar')
    this.animateProgressBar()
    this.handleVariantChange()
  }

  animateProgressBar () {
    if (!this.progressBar) return
    setTimeout(() => {
      this.progressBar.style.width = this.progressBar.dataset.width
    }, 250)
  }

  handleVariantChange () {
    window.addEventListener('kt.product.variant.change', async () => {
      const inner = this.querySelector('.product-block-stock-indicator-inner')
      if (inner) inner.style.opacity = '.25'
    })

    window.addEventListener('kt.product.variant.change.newDoc', async (e) => {
      const newDoc = e.detail
      const replacement = newDoc.querySelector('[id*="product-block-stock-indicator"]')
      if (replacement) {
        this.replaceWith(replacement)
        this.animateProgressBar()
      }
    })
  }
}
customElements.define('product-stock-indicator', ProductStockIndicator)

class ProductQuantityBreaks extends HTMLElement {
  constructor () {
    super()

    this.handleInputChange()
    this.handleVariantChange()
  }

  handleInputChange () {
    this.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', () => {
        const form = input.closest('form')
        if (!form) return
        const qtyField = form.querySelector('[name="quantity"]')
        if (qtyField) {
          qtyField.value = input.value
          qtyField.dispatchEvent(new CustomEvent('change'))
        }

        const atcBtn = form.querySelector('.btn-atc')
        if (atcBtn) {
          setTimeout(() => {
            atcBtn.classList.add('animate__animated', 'animate__shakeX')
          }, 250)

          setTimeout(() => {
            atcBtn.classList.remove('animate__animated', 'animate__shakeX')
          }, 1500)
        }
      })
    })
  }

  handleVariantChange () {
    window.addEventListener('kt.product.variant.change', async () => {
      const list = this.querySelector('.product-block-quantity-breaks-list')
      if (list) list.style.opacity = '.25'
    })

    window.addEventListener('kt.product.variant.change.newDoc', async (e) => {
      const newDoc = e.detail
      const replacement = newDoc.querySelector('product-quantity-breaks')
      if (replacement) {
        this.replaceWith(replacement)
        this.handleInputChange()
      }
    })
  }
}
customElements.define('product-quantity-breaks', ProductQuantityBreaks)

class ProductQuickButton extends HTMLElement {
  constructor () {
    super()

    this.btn = this.querySelector('.btn-quick-view')
    if (!this.btn) return

    this.btn.addEventListener('click', async (event) => this.fetchProductData(event))
  }

  async fetchProductData (event) {
    event.preventDefault()

    this.setLoading(true)

    try {
      const response = await fetch(this.btn.dataset.productUrl)
      if (response.ok) {
        const text = await response.text()
        const newDoc = new DOMParser().parseFromString(text, 'text/html')
        const modal = document.querySelector('product-quick-view-modal')
        if (modal) modal.showModal(newDoc)
      }
    } catch (e) {
      console.error(e)
    }

    this.setLoading(false)
  }

  setLoading (isLoading) {
    if (isLoading) {
      this.btn.classList.add('loading')
      this.btn.disabled = true
      this.btn.setAttribute('aria-busy', 'true')
    } else {
      this.btn.classList.remove('loading')
      this.btn.disabled = false
      this.btn.removeAttribute('aria-busy')
    }
  }
}
customElements.define('product-quick-view-button', ProductQuickButton)

class ProductQuickViewModal extends HTMLElement {
  constructor () {
    super()

    this.modalElem = document.querySelector('#quick-view-modal')
    if (!this.modalElem) return

    this.modalElem.addEventListener('hidden.bs.modal', () => {
      const productInner = this.modalElem.querySelector('.product')
      if (productInner) productInner.innerHTML = '<div data-quick-view-contents></div>'
    })

    window.addEventListener('kt.cart.added', () => {
      if (window.bootstrap?.Modal) {
        window.bootstrap.Modal.getOrCreateInstance(this.modalElem).hide()
      }
    })
  }

  async showModal (newDoc) {
    if (!this.modalElem) return

    const target = this.modalElem.querySelector('[data-quick-view-contents]')
    const source = newDoc.querySelector('.product-main [data-quick-view-contents]')
    if (target && source) target.replaceWith(source)

    this.modalElem.querySelector('.product-title')?.setAttribute('id', 'quick-view-modal-label')

    this.modalElem.querySelectorAll('.collapse-wrapper').forEach(collapseWrapper => {
      collapseWrapper.querySelector('[data-bs-toggle="collapse"]')?.setAttribute('aria-expanded', 'false')
      collapseWrapper.querySelector('.collapse')?.classList.remove('show')
    })

    this.modalElem.querySelectorAll('.product-options [name*="option"]').forEach(elem => {
      elem.setAttribute('name', `${elem.name}-qv`)
      elem.setAttribute('id', `${elem.id}-qv`)
    })

    this.modalElem.querySelectorAll('.product-options label').forEach(label => {
      const forAttr = label.getAttribute('for')
      if (forAttr) label.setAttribute('for', `${forAttr}-qv`)
    })

    this.modalElem.querySelectorAll('.collapse').forEach(elem => {
      elem.addEventListener('shown.bs.collapse', () => {
        elem.closest('.product-block-collapse')?.scrollIntoView({ behavior: 'smooth' })
      })
    })

    if (window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(this.modalElem).show()
    }
    window.dispatchEvent(new CustomEvent('kt.product.quick_view.modal_shown'))
  }
}
customElements.define('product-quick-view-modal', ProductQuickViewModal)

class ProductDropupSelector extends HTMLElement {
  constructor () {
    super()

    this.productCard = this.closest('.product-card')
    this.dropdownBtn = this.querySelector('.dropdown-toggle')
    const menu = this.querySelector('.dropdown-menu')

    if (this.productCard && this.dropdownBtn && menu) {
      menu.style.maxHeight = this.productCard.offsetHeight - this.dropdownBtn.offsetHeight - 2 + 'px'
    }

    this.addEventListener('show.bs.dropdown', (event) => {
      const isMobile = window.matchMedia('(max-width: 499px)').matches
      if (isMobile) {
        event.preventDefault()
        this.initOffcanvas()
      } else if (this.productCard) {
        const img = this.productCard.querySelector('.product-card-img-wrapper')
        if (img) img.style.opacity = '.2'
      }
    })

    this.addEventListener('hide.bs.dropdown', (event) => {
      const isMobile = window.matchMedia('(max-width: 499px)').matches
      if (isMobile) {
        event.preventDefault()
        this.removeOffcanvas()
      } else if (this.productCard) {
        const img = this.productCard.querySelector('.product-card-img-wrapper')
        if (img) img.style.opacity = '1'
      }
    })

    this.querySelectorAll('.dropdown-menu button').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn))
    })
  }

  async addToCart (btn) {
    if (window.bootstrap?.Dropdown) {
      window.bootstrap.Dropdown.getOrCreateInstance(this).hide()
    }

    this.dropdownBtn.classList.add('loading')
    this.dropdownBtn.disabled = true
    this.dropdownBtn.setAttribute('aria-busy', 'true')

    const items = [{
      id: Number(btn.dataset.variantId),
      quantity: 1
    }]

    const cartContainer = document.querySelector('cart-container')
    if (cartContainer && typeof cartContainer.add === 'function') {
      await cartContainer.add({ items })
    }

    this.dropdownBtn.classList.remove('loading')
    this.dropdownBtn.disabled = false
    this.dropdownBtn.setAttribute('aria-busy', 'false')
  }

  initOffcanvas () {
    const menu = this.querySelector('.dropdown-menu')
    const html = `
      <div id="product-dropup-select-offcanvas" class="offcanvas offcanvas-bottom" tabindex="-1" aria-labelledby="product-dropup-select-offcanvas-title">
        <div class="offcanvas-header">
          <h5 class="offcanvas-title" id="product-dropup-select-offcanvas-title">
            ${this.dropdownBtn.textContent}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" class="svg-icon-x" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="offcanvas-body p-0">
          <ul class="dropdown-menu">
            ${menu ? menu.innerHTML : ''}
          </ul>
        </div>
      </div>
    `
    document.body.insertAdjacentHTML('beforeend', html)

    if (window.bootstrap?.Offcanvas) {
      window.bootstrap.Offcanvas.getOrCreateInstance('#product-dropup-select-offcanvas').show()
    }

    document.querySelectorAll('#product-dropup-select-offcanvas .dropdown-menu button').forEach(btn => {
      btn.addEventListener('click', () => {
        this.addToCart(btn)
        if (window.bootstrap?.Offcanvas) {
          window.bootstrap.Offcanvas.getOrCreateInstance('#product-dropup-select-offcanvas').hide()
        }
      })
    })

    document.querySelector('#product-dropup-select-offcanvas')?.addEventListener('hidden.bs.offcanvas', () => {
      document.querySelector('#product-dropup-select-offcanvas')?.remove()
    })
  }

  removeOffcanvas () {
    document.querySelector('#product-dropup-select-offcanvas')?.remove()
  }
}
customElements.define('product-dropup-selector', ProductDropupSelector)

class CrossSells extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', () => this.onCheckboxChange(checkbox))
    })

    this.querySelectorAll('select[name="variant-id"]').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select))
    })

    this.querySelectorAll('.btn-atc').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn))
    })
  }

  onChangeVariant (select) {
    const selectedOption = select[select.selectedIndex]
    if (!selectedOption) return
    const variantImage = selectedOption.dataset.variantImage

    if (variantImage && variantImage.length) {
      const img = select.closest('.product-card')?.querySelector('.product-card-img-wrapper img')
      if (img) img.setAttribute('src', variantImage)
    }

    this.updateTotalPrice()
  }

  onCheckboxChange (checkbox) {
    const card = checkbox.closest('.product-card')
    if (card) {
      card.setAttribute('data-is-selected', checkbox.checked ? 'true' : 'false')
    }
    this.updateTotalPrice()
  }

  updateTotalPrice () {
    let totalPrice = 0
    let totalComparePrice = 0

    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      if (checkbox.checked) {
        const inputHidden = checkbox.closest('.product-card')?.querySelector('input[name="variant-id"][type="hidden"]')
        if (inputHidden) {
          totalPrice += Number(inputHidden.dataset.price || 0)
          totalComparePrice += Number(inputHidden.dataset.compareAtPrice || 0)
        }

        const select = checkbox.closest('.product-card')?.querySelector('select[name="variant-id"]')
        if (select && select[select.selectedIndex]) {
          totalPrice += Number(select[select.selectedIndex].dataset.price || 0)
          totalComparePrice += Number(select[select.selectedIndex].dataset.compareAtPrice || 0)
        }
      }
    })

    this.querySelectorAll('[data-total-price]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalPrice)}`.replace('.00', '')
    })

    this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalComparePrice)}`.replace('.00', '')
    })

    this.querySelectorAll('[data-total-savings]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalComparePrice - totalPrice)}`.replace('.00', '')
    })

    const atcBtn = this.querySelector('.btn-atc')
    if (atcBtn) {
      atcBtn.disabled = totalPrice === 0
    }

    if (totalComparePrice > totalPrice) {
      this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
        elem.closest('s')?.removeAttribute('hidden')
      })
      this.querySelectorAll('[data-total-savings]').forEach(elem => {
        elem.closest('.cross-sells-footer-total-savings')?.removeAttribute('hidden')
      })
    } else {
      this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
        elem.closest('s')?.setAttribute('hidden', 'hidden')
      })
      this.querySelectorAll('[data-total-savings]').forEach(elem => {
        elem.closest('.cross-sells-footer-total-savings')?.setAttribute('hidden', 'hidden')
      })
    }
  }

  async addToCart (atcBtn) {
    atcBtn.style.width = `${atcBtn.clientWidth}px`
    atcBtn.classList.add('loading')
    atcBtn.disabled = true
    atcBtn.setAttribute('aria-busy', 'true')

    const items = []

    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      if (checkbox.checked) {
        const idElem = checkbox.closest('.product-card')?.querySelector('[name="variant-id"]')
        if (idElem) {
          items.push({
            id: Number(idElem.value),
            quantity: 1
          })
        }
      }
    })

    const cartContainer = document.querySelector('cart-container')
    if (cartContainer && typeof cartContainer.add === 'function') {
      await cartContainer.add({ items })
    }

    atcBtn.style.width = ''
    atcBtn.classList.remove('loading')
    atcBtn.disabled = false
    atcBtn.setAttribute('aria-busy', 'false')
  }
}
customElements.define('cross-sells', CrossSells)

class ProductCard extends HTMLElement {
  constructor () {
    super()
    this.handleCarouselHover()
  }

  handleCarouselHover () {
    if (window.matchMedia('(pointer: coarse)').matches) return

    const carouselElem = this.querySelector('.product-card-img-wrapper .carousel')
    const carouselInner = this.querySelector('.product-card-img-wrapper .carousel-inner')
    if (!carouselElem || !carouselInner || !window.bootstrap?.Carousel) return

    const bsCarousel = window.bootstrap.Carousel.getOrCreateInstance(carouselElem)
    let myInterval

    carouselInner.addEventListener('mouseenter', () => {
      setTimeout(() => {
        bsCarousel.next()
      }, 500)

      myInterval = setInterval(() => {
        bsCarousel.next()
      }, 2000)
    })

    carouselInner.addEventListener('mouseleave', () => {
      clearInterval(myInterval)
    })
  }
}
customElements.define('product-card', ProductCard)

class FileUploadPersonalizationField extends HTMLElement {
  constructor () {
    super()
    this.loadStyleScripts()

    const vendorScript = document.querySelector('script[src*="plugin-image-preview"]')
    if (vendorScript) {
      vendorScript.onload = () => this.init()
    }

    window.addEventListener('kt.product.quick_view.modal_shown', () => this.init())
  }

  loadStyleScripts () {
    if (document.querySelector('script[src*="plugin-image-preview"]')) return

    const style1 = document.createElement('link')
    style1.setAttribute('href', 'https://cdn.jsdelivr.net/npm/filepond@4.31.1/dist/filepond.min.css')
    style1.setAttribute('rel', 'stylesheet')
    style1.setAttribute('integrity', 'sha256-a95jYCBL4++k1XyLYgulKmY33bIJIVYMsJO/RNytaJM=')
    style1.setAttribute('crossorigin', 'anonymous')
    document.head.appendChild(style1)

    const style2 = document.createElement('link')
    style2.setAttribute('href', 'https://cdn.jsdelivr.net/npm/filepond-plugin-image-preview@4.6.12/dist/filepond-plugin-image-preview.min.css')
    style2.setAttribute('rel', 'stylesheet')
    style2.setAttribute('integrity', 'sha256-YsO8aMI20vSizdmc2mmWx2DU1wof4v60Nwy7hIBvdM8=')
    style2.setAttribute('crossorigin', 'anonymous')
    document.head.appendChild(style2)

    const script1 = document.createElement('script')
    script1.setAttribute('src', 'https://cdn.jsdelivr.net/npm/filepond@4.31.1/dist/filepond.min.js')
    script1.setAttribute('integrity', 'sha256-6yXpr8+sATA4Q2ANTyZmpn4ZGP7grbIRNpe9s0Y+iO0=')
    script1.setAttribute('crossorigin', 'anonymous')
    script1.setAttribute('defer', 'defer')
    document.head.appendChild(script1)

    const script2 = document.createElement('script')
    script2.setAttribute('src', 'https://cdn.jsdelivr.net/npm/filepond-plugin-image-preview@4.6.12/dist/filepond-plugin-image-preview.min.js')
    script2.setAttribute('integrity', 'sha256-1vQHytMrpOsKFyUUPuEVhxeNH6UhMX3uNetDRvlSpwU=')
    script2.setAttribute('crossorigin', 'anonymous')
    script2.setAttribute('defer', 'defer')
    document.head.appendChild(script2)
  }

  async init () {
    await new Promise(resolve => setTimeout(resolve, 200))

    if (!window.FilePond) return
    if (window.FilePondPluginImagePreview) {
      window.FilePond.registerPlugin(window.FilePondPluginImagePreview)
    }
    const inputElem = this.querySelector('input[type="file"]')
    const labelElem = this.querySelector('[data-label-text]')

    if (inputElem) {
      window.FilePond.create(inputElem, {
        storeAsFile: true,
        labelIdle: labelElem ? labelElem.innerHTML : 'Browse files'
      })
    }
  }
}
customElements.define('file-upload-personalization-field', FileUploadPersonalizationField)

// Product block collapses
if (window.matchMedia('(max-width: 1199px)').matches) {
  document.querySelectorAll('.product-block-collapse').forEach(elem => {
    elem.querySelector('.collapse')?.addEventListener('shown.bs.collapse', () => {
      elem.scrollIntoView({ behavior: 'smooth' })
    })
  })
}

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)